import { Injectable, signal, computed, inject } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { STORAGE_KEYS } from '../../config/storage-keys.config';
import {
  PushNotificationApiService,
  type NotificationTargetPortal,
  type PushNotificationItem,
  type PaginatedNotifications,
} from '../push-notification/push-notification-api.service';

export interface AppNotification {
  id: string;
  title: string;
  body: string;
  /** Legacy HTML body used by local-only notifications. */
  message?: string;
  time: string;
  createdAt: string;
  isRead: boolean;
  isLocal?: boolean;
}

const DEFAULT_PORTAL: NotificationTargetPortal = 'CUSTOMER';
/** Initial page size and each scroll page size. */
const LIST_LIMIT = 10;

interface NotificationListState {
  currentPage: number;
  total: number;
  hasMore: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly pushNotificationApi = inject(PushNotificationApiService);

  private readonly unreadNotificationsSignal = signal<AppNotification[]>([]);
  private readonly readNotificationsSignal = signal<AppNotification[]>([]);
  private readonly unreadCountSignal = signal(0);
  private readonly loadingSignal = signal(false);
  private readonly unreadLoadingMoreSignal = signal(false);
  private readonly readLoadingMoreSignal = signal(false);
  private readonly unreadHasMoreSignal = signal(true);
  private readonly readHasMoreSignal = signal(true);
  private readonly initialLoadCompleteSignal = signal(false);
  private readonly targetPortalSignal =
    signal<NotificationTargetPortal>(DEFAULT_PORTAL);
  private readonly customerIdSignal = signal<string | null>(null);

  private unreadState: NotificationListState = {
    currentPage: 1,
    total: 0,
    hasMore: true,
  };
  private readState: NotificationListState = {
    currentPage: 1,
    total: 0,
    hasMore: true,
  };

  readonly unreadNotifications = computed(() => this.unreadNotificationsSignal());
  readonly readNotifications = computed(() => this.readNotificationsSignal());
  readonly unreadCount = computed(() => this.unreadCountSignal());
  readonly loading = computed(() => this.loadingSignal());
  readonly unreadLoadingMore = computed(() => this.unreadLoadingMoreSignal());
  readonly readLoadingMore = computed(() => this.readLoadingMoreSignal());
  readonly unreadHasMore = computed(() => this.unreadHasMoreSignal());
  readonly readHasMore = computed(() => this.readHasMoreSignal());
  readonly isReady = computed(() => this.initialLoadCompleteSignal());
  readonly targetPortal = computed(() => this.targetPortalSignal());
  readonly customerId = computed(() => this.customerIdSignal());
  readonly hasNotifications = computed(
    () =>
      this.unreadNotificationsSignal().length > 0 ||
      this.readNotificationsSignal().length > 0,
  );

  setTargetPortal(portal: NotificationTargetPortal): void {
    this.targetPortalSignal.set(portal);
  }

  setCustomerId(customerId: string | null | undefined): void {
    const trimmed = customerId?.trim() || null;
    this.customerIdSignal.set(trimmed);
  }

  /**
   * Resolves customer scope from login `id` (auth user id), falling back to
   * workspace `customer_id` when present.
   */
  resolveCustomerIdFromAuth(): string | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    const authId = localStorage.getItem(STORAGE_KEYS.AUTH_USER_ID)?.trim();
    if (authId) {
      return authId;
    }
    return localStorage.getItem(STORAGE_KEYS.CUSTOMER_ID)?.trim() || null;
  }

  /**
   * Reloads page 1 for unread + read lists and unread count.
   */
  refresh(): void {
    if (this.loadingSignal()) {
      return;
    }

    const targetPortal = this.targetPortalSignal();
    this.loadingSignal.set(true);
    this.initialLoadCompleteSignal.set(false);
    this.resetListState();

    forkJoin({
      unreadList: this.fetchPage(targetPortal, false, 1),
      readList: this.fetchPage(targetPortal, true, 1),
      unread: this.pushNotificationApi
        .getUnreadCount({
          targetPortal,
        })
        .pipe(catchError(() => of({ count: 0 }))),
    })
      .pipe(finalize(() => this.loadingSignal.set(false)))
      .subscribe(({ unreadList, readList, unread }) => {
        const localUnread = this.getLocalUnread();
        this.unreadNotificationsSignal.set([
          ...localUnread,
          ...unreadList.items,
        ]);
        this.readNotificationsSignal.set(readList.items);
        this.applyListState(false, unreadList);
        this.applyListState(true, readList);
        this.unreadCountSignal.set((unread.count ?? 0) + localUnread.length);
        this.initialLoadCompleteSignal.set(true);
      });
  }

  loadMoreUnread(): void {
    this.loadMoreForList(false);
  }

  loadMoreRead(): void {
    this.loadMoreForList(true);
  }

  /**
   * Adds a local-only notification (not persisted via API).
   */
  addNotification(message: string): void {
    const now = new Date().toISOString();
    const localItem: AppNotification = {
      id: `local-${Date.now()}`,
      title: '',
      body: '',
      message,
      time: formatTimeAgo(now),
      createdAt: now,
      isRead: false,
      isLocal: true,
    };

    this.unreadNotificationsSignal.update((current) => [localItem, ...current]);
    this.unreadCountSignal.update((count) => count + 1);
  }

  markAsRead(id: string): void {
    const target = this.unreadNotificationsSignal().find((n) => n.id === id);
    if (!target || target.isRead) {
      return;
    }

    const readItem: AppNotification = { ...target, isRead: true };
    this.unreadNotificationsSignal.update((current) =>
      current.filter((n) => n.id !== id),
    );
    this.readNotificationsSignal.update((current) => {
      if (current.some((n) => n.id === id)) {
        return current;
      }
      return [readItem, ...current];
    });
    this.unreadCountSignal.update((count) => Math.max(0, count - 1));

    if (target.isLocal) {
      return;
    }

    this.pushNotificationApi
      .markRead(id)
      .pipe(
        catchError(() => {
          this.readNotificationsSignal.update((current) =>
            current.filter((n) => n.id !== id),
          );
          this.unreadNotificationsSignal.update((current) => [
            target,
            ...current,
          ]);
          this.unreadCountSignal.update((count) => count + 1);
          return of(null);
        }),
      )
      .subscribe();
  }

  markAllAsRead(): void {
    const targetPortal = this.targetPortalSignal();
    const customerId =
      this.customerIdSignal() || this.resolveCustomerIdFromAuth();
    const hadUnread = this.unreadCountSignal() > 0;
    const unreadItems = this.unreadNotificationsSignal().map((n) => ({
      ...n,
      isRead: true,
    }));

    this.readNotificationsSignal.update((current) => {
      const existingIds = new Set(current.map((n) => n.id));
      const moved = unreadItems.filter((n) => !existingIds.has(n.id));
      return [...moved, ...current];
    });
    this.unreadNotificationsSignal.set([]);
    this.unreadCountSignal.set(0);
    this.unreadState = { currentPage: 1, total: 0, hasMore: false };
    this.unreadHasMoreSignal.set(false);

    if (!hadUnread) {
      return;
    }

    this.pushNotificationApi
      .markAllRead({
        targetPortal,
        ...(customerId ? { customerId } : {}),
      })
      .pipe(
        catchError(() => {
          this.refresh();
          return of(null);
        }),
      )
      .subscribe();
  }

  clearNotifications(): void {
    this.unreadNotificationsSignal.set([]);
    this.readNotificationsSignal.set([]);
    this.unreadCountSignal.set(0);
    this.resetListState();
    this.initialLoadCompleteSignal.set(false);
  }

  private loadMoreForList(isRead: boolean): void {
    if (
      !this.initialLoadCompleteSignal() ||
      this.loadingSignal() ||
      (isRead
        ? this.readLoadingMoreSignal()
        : this.unreadLoadingMoreSignal())
    ) {
      return;
    }

    const state = isRead ? this.readState : this.unreadState;
    if (!state.hasMore) {
      return;
    }

    const nextPage = state.currentPage + 1;
    const targetPortal = this.targetPortalSignal();
    const loadingSignal = isRead
      ? this.readLoadingMoreSignal
      : this.unreadLoadingMoreSignal;

    loadingSignal.set(true);

    this.fetchPage(targetPortal, isRead, nextPage)
      .pipe(finalize(() => loadingSignal.set(false)))
      .subscribe((result) => {
        if (result.items.length === 0) {
          state.hasMore = false;
          this.syncHasMoreSignal(isRead, false);
          return;
        }

        const targetSignal = isRead
          ? this.readNotificationsSignal
          : this.unreadNotificationsSignal;

        targetSignal.update((current) => {
          const existingIds = new Set(current.map((n) => n.id));
          const unique = result.items.filter((n) => !existingIds.has(n.id));
          return [...current, ...unique];
        });
        this.applyListState(isRead, result, nextPage);
      });
  }

  private fetchPage(
    targetPortal: NotificationTargetPortal,
    isRead: boolean,
    page: number,
  ) {
    return this.pushNotificationApi
      .getNotifications({
        page,
        limit: LIST_LIMIT,
        targetPortal,
        isRead,
      })
      .pipe(
        catchError(() => of(emptyPage())),
        map((list) =>
          mapPaginatedToResult(list, (item) => this.mapApiNotification(item)),
        ),
      );
  }

  private applyListState(
    isRead: boolean,
    result: PageFetchResult,
    page = 1,
  ): void {
    const state = isRead ? this.readState : this.unreadState;
    state.currentPage = Math.max(1, page);
    state.total = result.total;

    const loadedCount = isRead
      ? this.readNotificationsSignal().length
      : this.unreadNotificationsSignal().filter((n) => !n.isLocal).length;

    if (state.total > 0) {
      state.hasMore = loadedCount < state.total;
      this.syncHasMoreSignal(isRead, state.hasMore);
      return;
    }

    state.hasMore = result.items.length >= LIST_LIMIT;
    this.syncHasMoreSignal(isRead, state.hasMore);
  }

  private resetListState(): void {
    this.unreadState = { currentPage: 1, total: 0, hasMore: true };
    this.readState = { currentPage: 1, total: 0, hasMore: true };
    this.unreadHasMoreSignal.set(true);
    this.readHasMoreSignal.set(true);
  }

  private syncHasMoreSignal(isRead: boolean, hasMore: boolean): void {
    if (isRead) {
      this.readHasMoreSignal.set(hasMore);
      return;
    }
    this.unreadHasMoreSignal.set(hasMore);
  }

  private getLocalUnread(): AppNotification[] {
    return this.unreadNotificationsSignal().filter((n) => n.isLocal);
  }

  private mapApiNotification(item: PushNotificationItem): AppNotification {
    const createdAt = item.createdAt || new Date().toISOString();
    return {
      id: item.id,
      title: item.title ?? '',
      body: item.body ?? '',
      time: formatTimeAgo(createdAt),
      createdAt,
      isRead: !!item.isRead,
      isLocal: false,
    };
  }
}

interface PageFetchResult {
  items: AppNotification[];
  total: number;
}

function emptyPage(): PaginatedNotifications {
  return { data: [], total: 0, page: 1, limit: LIST_LIMIT, totalPages: 0 };
}

function mapPaginatedToResult(
  list: PaginatedNotifications,
  mapper: (item: PushNotificationItem) => AppNotification,
): PageFetchResult {
  return {
    items: (list.data ?? []).map(mapper),
    total: Number(list.total ?? 0),
  };
}

export function formatTimeAgo(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.max(0, Math.floor(diffMs / 1000));

  if (diffSec < 60) {
    return 'Just now';
  }

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return `${diffMin} min ago`;
  }

  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) {
    return `${diffHour}h ago`;
  }

  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 30) {
    return `${diffDay}d ago`;
  }

  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12) {
    return `${diffMonth}mo ago`;
  }

  const diffYear = Math.floor(diffDay / 365);
  return `${diffYear}y ago`;
}
